import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '@/lib/react-query'
import { STORAGE_KEYS } from '@/shared/constants'
import { GAPI_FONT_URL, GoogleWebFont, GoogleWebFontResponse, POPULAR_GOOGLE_FONTS } from '@/shared/gapi'

/**
 * miscellaneous context to cache request result from 3rd party api like:
 * - google fonts
 * -
 */
interface CacheContextValue {
  allFonts: GoogleWebFont[]
  isFontLoading: boolean
  loadAllFonts: () => void
  imageCache: {
    values: Record<string, number>
    update: (id: string, timestamp: number) => void
    remove: (id: string) => void
  }
}

export const WASM_NAME = 'makyo.wasm'

const CacheContext = React.createContext<CacheContextValue>({
  allFonts: [],
  isFontLoading: false,
  loadAllFonts: () => {},
  imageCache: {
    values: {},
    update: () => {},
    remove: () => {},
  },
})

export function useCacheContext() {
  return React.useContext(CacheContext)
}

export function CacheProvider({ children }: { children: React.ReactNode }) {
  // Load popular fonts first, then load all fonts in background
  const popularFontsQuery = useQuery({
    queryKey: [queryKeys.popularFonts],
    queryFn: () => Promise.resolve(POPULAR_GOOGLE_FONTS),
    staleTime: Infinity,
  })

  const allFontsQuery = useQuery({
    queryKey: [queryKeys.allFonts],
    queryFn: async () => {
      const res: GoogleWebFontResponse = await fetch(GAPI_FONT_URL).then((res) => res.json())
      return res.items
    },
    enabled: false, // Load on demand
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  })

  const loadAllFonts = useCallback(() => {
    allFontsQuery.refetch()
  }, [allFontsQuery])

  // Access google fonts
  const fontQuery = useQuery({
    queryKey: [queryKeys.fonts],
    queryFn: async () => {
      const res: GoogleWebFontResponse = await fetch(GAPI_FONT_URL).then((res) => res.json())
      return res.items
    },
    initialData: POPULAR_GOOGLE_FONTS,
  })

  const projectIconStorage = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.IMAGE_CACHE) || '{}')

  /**
   * Store for project icon timestamp cache
   */
  const imageCache = useMemo(() => {
    return {
      values: projectIconStorage,
      update: (id: string, timestamp: number) => {
        let storage = projectIconStorage
        storage[id] = timestamp
        sessionStorage.setItem(STORAGE_KEYS.IMAGE_CACHE, JSON.stringify(storage))
      },
      remove: (id: string) => {
        let storage = projectIconStorage
        if (storage && storage[id]) {
          delete storage[id]
          sessionStorage.setItem(STORAGE_KEYS.IMAGE_CACHE, JSON.stringify(storage))
        }
      },
    }
  }, [projectIconStorage])

  return (
    <CacheContext.Provider
      value={{
        allFonts: allFontsQuery.data ?? popularFontsQuery.data ?? [],
        isFontLoading: allFontsQuery.isLoading,
        loadAllFonts,
        imageCache,
      }}
    >
      {children}
    </CacheContext.Provider>
  )
}
